<template>
    <div class="slider-container" ref="slider" 
        @touchstart="startTouch" 
        @touchmove="moveTouch" 
        @touchend="endDrag"
        @mousedown="startDrag" 
        @mousemove="moveDrag" 
        @mouseup="endDrag" 
        @mouseleave="endDrag">
        <div class="slider-content" ref="content" :style="sliderStyle">
            <div v-for="(name, id) in items" :key="id" class="slider-item" 
                @click="selectItem(id)"
                :class="{ 'selected': selectedItem === id }">
                {{ name }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        items: {
            type: Array,
            required: true,
        },
        value: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            isDragging: false,
            startX: 0,
            currentTranslate: 0,
            prevTranslate: 0,
            selectedItem: 0,
            animationFrame: null,
            maxTranslate: 0,
            minTranslate: 0
        };
    },
    computed: {
        sliderStyle() {
            return {
                transform: `translateX(${this.currentTranslate}px)`,
                transition: this.isDragging ? 'none' : 'transform 0.4s ease-out'
            };
        },
    },
    mounted() {
        this.calculateBoundaries();
        window.addEventListener('resize', this.calculateBoundaries);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.calculateBoundaries);
    },
    methods: {
        calculateBoundaries() {
            const containerWidth = this.$refs.slider.offsetWidth;
            const contentWidth = this.$refs.content.scrollWidth;
            this.maxTranslate = 0;
            this.minTranslate = containerWidth - contentWidth;
        },
        startTouch(event) {
            this.startX = event.touches[0].clientX;
            this.isDragging = true;
        },
        moveTouch(event) {
            if (!this.isDragging) return;
            event.preventDefault();
            const currentX = event.touches[0].clientX;
            const movementX = currentX - this.startX;
            this.currentTranslate = Math.max(this.minTranslate, Math.min(this.maxTranslate, this.prevTranslate + movementX));
        },
        startDrag(event) {
            event.preventDefault();
            this.isDragging = true;
            this.startX = event.clientX;
            this.animationFrame = requestAnimationFrame(this.moveDrag);
        },
        moveDrag(event) {
            if (!this.isDragging) return;
            const currentX = event.clientX;
            const movementX = currentX - this.startX;
            this.currentTranslate = Math.max(this.minTranslate, Math.min(this.maxTranslate, this.prevTranslate + movementX));
        },
        endDrag() {
            if (this.isDragging) {
                this.isDragging = false;
                this.prevTranslate = this.currentTranslate;
                cancelAnimationFrame(this.animationFrame);
            }
        },
        selectItem(id) {
            this.selectedItem = id;
            this.$emit('input', this.items[id]);
        }
    },
    watch: {
        items(newItems) {
            // Recalculate boundaries when items change
            this.$nextTick(() => {
                this.calculateBoundaries();
            });
        },
        value(newVal) {
            const index = this.items.findIndex(item => item === newVal);
            if (index !== -1) {
                this.selectedItem = index;
            }
        }
    }
};
</script>

<style scoped>
.slider-container {
    overflow: hidden;
    cursor: grab;
    min-height: 40px;
    user-select: none; /* Prevent text selection during drag */
}

.slider-content {
    display: flex;
    font-size: 16px;
}

.slider-item {
    white-space: nowrap; 
    padding: 12px 20px;
    text-align: center;
    cursor: pointer;
    border: 1px solid #BFBFBF;
    background-color: white;
    transition: background-color 0.3s ease;
}

.selected {
    background-color: #F47325cc;
    color: white;
    border-color: #F47325;
}

.slider-item:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
}

.slider-item:last-child {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
}
</style>
